"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Trophy, Users, MapPin, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const timelineEvents = [{
  year: "Puppy",
  title: "Early Development",
  description: "Focusing on essential vaccinations, social skills, and nutritional foundations to ensure your new companion starts life on the right paw.",
  icon: Users,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_puppy_care.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_puppy_care.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_puppy_care.png-wvc-sizes",
    alt: "A playful puppy receiving a checkup"
  }
}, {
  year: "Adult",
  title: "Peak Wellness",
  description: "Maintaining vitality through regular dental cleanings, weight management, and preventative screenings tailored to their active lifestyle.",
  icon: Star,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_adult_dog.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_adult_dog.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_adult_dog.png-wvc-sizes",
    alt: "Healthy adult dog in an urban park"
  }
}, {
  year: "Senior",
  title: "Graceful Aging",
  description: "Specialized care for joint health, metabolic monitoring, and comfort management to ensure their golden years are happy and pain-free.",
  icon: Trophy,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_senior_cat.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_senior_cat.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_senior_cat.png-wvc-sizes",
    alt: "Senior cat resting comfortably"
  }
}, {
  year: "Care",
  title: "Lifelong Support",
  description: "Our commitment to your pet's health never wavers, providing a continuum of care that adapts as their needs evolve over time.",
  icon: MapPin,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_vet_exam.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_vet_exam.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687484/img/city_pet_vet_exam.png-wvc-sizes",
    alt: "Veterinarian examining a pet"
  }
}];
function TimelineItem({
  item,
  index,
  isLast
}: {
  item: (typeof timelineEvents)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const Icon = item.icon;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row w-full group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      {!isLast && <div className="absolute left-6 md:left-1/3 top-12 bottom-[-3rem] w-px bg-gradient-to-b from-primary/30 to-transparent md:-translate-x-1/2 z-0 border-l-2 border-dotted border-primary/40" />}

      {}
      <div className="absolute left-6 md:left-1/3 top-2 md:top-4 w-4 h-4 rounded-lg bg-background border-2 border-primary md:-translate-x-1/2 z-10 group-hover:bg-primary group-hover:scale-125 transition-all duration-500 shadow-md" />

      {}
      <div className="w-full md:w-1/3 pl-16 md:pl-0 md:pr-16 pt-0 md:pt-2 text-left md:text-right z-10">
        <h3 className="text-5xl md:text-7xl lg:text-8xl font-bold font-default tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-primary via-primary/60 to-primary/10 opacity-60 group-hover:opacity-100 transition-opacity duration-500">
          {item.year}
        </h3>
      </div>

      {}
      <div className="w-full md:w-2/3 pl-16 md:pl-16 pb-16 md:pb-24 pt-4 md:pt-0 z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-accent text-accent-foreground">
            <Icon className="w-5 h-5" />
          </div>
          <h4 className="text-2xl md:text-3xl font-bold font-default text-foreground">
            {item.title}
          </h4>
        </div>
        
        <p className="text-muted-foreground text-lg mb-8 max-w-2xl leading-relaxed font-default">
          {item.description}
        </p>

        {}
        <div className="relative rounded-3xl overflow-hidden aspect-video md:aspect-[21/9] max-w-3xl shadow-xl border border-border/50 group-hover:border-primary/20 transition-colors duration-500 bg-card backdrop-blur-md">
          <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" className="w-full h-full object-cover grayscale-[0.3] brightness-105 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700 scale-105 group-hover:scale-100" />
          {}
          <div className="absolute inset-0 bg-primary/5 mix-blend-multiply group-hover:opacity-0 transition-opacity duration-700 pointer-events-none" />
        </div>
      </div>
    </div>;
}
export default function TheCityPetCareJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="245989" id="the-city-pet-care-journey" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-lg bg-secondary/40 blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4 font-default">
            Our Care Philosophy
          </h2>
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default mb-6 text-foreground tracking-tight">
            The City Pet Care Journey
          </h3>
          <p className="text-xl text-muted-foreground font-default leading-relaxed">
            From the first playful pounce to the gentle golden years, we provide a roadmap of wellness tailored to every stage of your pet's life.
          </p>
        </div>

        {}
        <div className="relative">
          {timelineEvents.map((item, index) => <TimelineItem key={index} item={item} index={index} isLast={index === timelineEvents.length - 1} />)}
        </div>

        {}
        <div className="mt-16 md:mt-24 text-center relative z-20">
          <Link to="/WellnessServicesPage">
            <Button size="lg" className="group text-lg px-8 py-6 rounded-lg shadow-lg shadow-primary/10 hover:shadow-primary/20 transition-all duration-300 bg-primary text-primary-foreground">
              Explore Wellness Services
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}